import { FormEvent, useMemo, useState } from "react";
import { posthog } from '../../../lib/posthog';
import { signUpWithEmail } from "../api/adminAuthApi";
import {
  lookupWorkspaceAccessByEmail,
  submitWorkspaceAccessRequest,
} from "../api/adminWorkspaceAccessApi";
import { AuthField } from "../components/AuthField";
import { AuthPasswordField } from "../components/AuthPasswordField";
import { AuthShell } from "../components/AuthShell";
import { AuthStatusMessage } from "../components/AuthStatusMessage";
import { AuthSubmitButton } from "../components/AuthSubmitButton";
import { navigateTo } from "../components/authNavigation";

function getVerifyEmailPath(
  email: string,
  flow: "sign-in" | "sign-up",
): string {
  const params = new URLSearchParams({
    email,
    flow,
  });

  return `/admin/verify-email?${params.toString()}`;
}

export function SignUpPage() {
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const workspaceAccessLookup = useMemo(
    () => lookupWorkspaceAccessByEmail({ email }),
    [email],
  );
  const requiresRequestAccess =
    workspaceAccessLookup.ok &&
    workspaceAccessLookup.data.classification === "company_existing";
  const requestAccessWorkspaceName =
    workspaceAccessLookup.ok ? workspaceAccessLookup.data.workspaceName : null;
  const signupSubtitle = requiresRequestAccess
    ? `This company domain is already connected to ${requestAccessWorkspaceName ?? "an existing workspace"}. Submit a request and a workspace admin will review it before sending an invitation.`
    : "Sign up to manage 3D manual projects, assets, and release workflows. New company domains create a branded workspace after verification.";

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!fullName.trim() || !companyName.trim() || !email.trim()) {
      setError("Full name, company name, and email are required.");
      return;
    }

    posthog.capture('auth_sign_up_attempted', { email, company_name: companyName });
    setIsSubmitting(true);

    try {
      if (requiresRequestAccess) {
        const result = await submitWorkspaceAccessRequest({
          email,
          fullName,
          companyName,
        });

        if (!result.ok) {
          setError(result.error.message);
          return;
        }

        setSuccess(
          `Request sent to ${result.data.workspaceName ?? "the workspace"} admins. They'll email an invitation after approval.`,
        );
        return;
      }

      if (password !== confirmPassword) {
        setError("Passwords must match before you continue.");
        return;
      }

      const result = await signUpWithEmail({
        email,
        password,
        fullName,
        companyName,
      });

      if (!result.ok) {
        setError(result.error.message);
        return;
      }

      setSuccess(
        `Account created for ${companyName || "your team"}. Redirecting to verification...`,
      );
      navigateTo(getVerifyEmailPath(email, "sign-up"));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Create Your Workspace Account"
      subtitle={signupSubtitle}
      badgeIcon="lock_open"
      badgeLabel="Metaphysical Labs"
      data-oid="2vnmckx"
    >
      <form className="auth-form" onSubmit={handleSubmit} data-oid="k4xbrgp">
        <AuthField
          label="Full Name"
          name="fullName"
          type="text"
          autoComplete="name"
          placeholder="Jet Mejia"
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
          data-oid="q:2ve27"
        />

        <AuthField
          label="Company Name"
          name="companyName"
          type="text"
          autoComplete="organization"
          placeholder="Metaphysical Labs"
          value={companyName}
          onChange={(event) => setCompanyName(event.target.value)}
          data-oid=".7ic::v"
        />

        <AuthField
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="owner@example.com"
          leadingIcon="mail"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          data-oid="nepoksc"
        />

        {workspaceAccessLookup.ok &&
        workspaceAccessLookup.data.classification === "personal" ? (
          <AuthStatusMessage
            tone="info"
            message="Using a personal email? No problem — a workspace will be created for you after verification."
            data-oid="1bc8nl8"
          />
        ) : null}
        {workspaceAccessLookup.ok &&
        workspaceAccessLookup.data.classification === "company_new" ? (
          <AuthStatusMessage
            tone="success"
            message="New company domains create a branded workspace automatically after verification."
            data-oid="6i5z7qi"
          />
        ) : null}
        {requiresRequestAccess ? (
          <AuthStatusMessage
            tone="success"
            message={`This email domain already belongs to ${requestAccessWorkspaceName ?? "an existing workspace"}. Request access instead of creating a second workspace.`}
            data-oid="ww5i74n"
          />
        ) : null}

        {!requiresRequestAccess ? (
          <>
            <AuthPasswordField
              label="Password"
              name="password"
              autoComplete="new-password"
              placeholder="Create a strong password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              data-oid="mool8w0"
            />

            <AuthPasswordField
              label="Confirm Password"
              name="confirmPassword"
              autoComplete="new-password"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              error={error}
              data-oid="54u2__g"
            />
          </>
        ) : null}

        {error ? (
          <AuthStatusMessage tone="error" message={error} data-oid="hn-9zxo" />
        ) : null}
        {success ? (
          <AuthStatusMessage
            tone="success"
            message={success}
            data-oid="z4vokim"
          />
        ) : null}
        <AuthSubmitButton
          type="submit"
          isBusy={isSubmitting}
          busyLabel={
            requiresRequestAccess ? "Sending Request..." : "Creating Account..."
          }
          data-oid="n0dz6uq"
        >
          {requiresRequestAccess ? "Request Access" : "Create Account"}
        </AuthSubmitButton>
      </form>
      <p className="auth-footer-copy" data-oid="yb1_j4-">
        Already have access?{" "}
        <button
          className="auth-link-button"
          type="button"
          onClick={() => navigateTo("/admin/sign-in")}
          data-oid="-8oi1zs"
        >
          Sign In
        </button>
      </p>
    </AuthShell>
  );
}
